import React, { useEffect, useMemo, useState } from 'react';
import Iframe from 'react-iframe';
import { connect } from 'react-redux';
import PayForm from './PayForm';
import PayDetails from './PayDetails';
import { Parsers } from '../../helpers/formFields';
import { useForm } from '../../hooks/useForm';
import { apiService } from '../../services';
import { Validators } from '../../helpers';
import { PaymentPageOpts, BY_SMS, BY_EMAIL } from '../../helpers/payments';
import { UpdateError, CollectPaymentError, ExelotError } from '../../helpers/fetchErrors';
import PayDutiesError from './PayDutiesError';

const emailErrorMessage = 'יש להזין מייל תקין';
const idNumberErrorMessage = 'יש להזין תעודת זהות תקינה';

const getInitalValues = () => ({
  email: '',
});

const PayDuties = (props) => {
  const {
    getPayDutiesParcelAPI,
    updatePayDutiesParcelAPI,
    collectPayDutiesParcelAPI,
    history,
    match
  } = props;

  const [showForm, setShowForm] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [parcelId, setParcelId] = useState();
  const [parcelData, setParcelData] = useState(null);
  const [errorMessage, setError] = useState(null);
  const [creditGuardUrl, setCreditGuardUrl] = useState();
  const query = useMemo(() => new URLSearchParams(location.search || ''));
  let linkBy = 'unknown';
  let commLinkID = '';
  if (query.get(BY_SMS)) {
    linkBy = 'sms';
    commLinkID = query.get(BY_SMS);
  } else if (query.get(BY_EMAIL)) {
    linkBy = 'email';
    commLinkID = query.get(BY_EMAIL);
  }

  function handleCGPaymentCompletion(opts = {}) {
    console.log('Handling PayDuties CG Payment Completion callback');
    const { data } = opts;
    if (!data) {
      console.error('No data found');
      return;
    }

    const { status, page, res } = data;
    if (PaymentPageOpts[page] !== PaymentPageOpts.payDuties) {
      console.error(`Unexpected page ${page}`);
      return;
    }
    history.push(`/${page}/callback/${status}`, { ...res });
  }

  useEffect(() => {
    console.log('Adding PayDuties listener');
    window.addEventListener('message', handleCGPaymentCompletion, false);

    return () => {
      console.log('Removing PayDuties listener');
      window.removeEventListener('message', handleCGPaymentCompletion, false);
    };
  }, []);

  const { fields, setValue, errors, setErrors, resetError } = useForm(getInitalValues);

  useEffect(() => {
    if (!parcelData) {
      return;
    }

    const { hawb, isAlreadyPaid, buyer: { email } } = parcelData;

    if (isAlreadyPaid) {
      history.push('/payDuties/callback/success', { hawb });
      return;
    }

    if (email) {
      setValue(email, 'email');
    }
  }, [parcelData]);

  useEffect(() => {
    const id = Parsers.sanitizeString(match.params.id);
    if (!id) {
      setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      return;
    }

    setParcelId(id);

    apiService
      .callServer(getPayDutiesParcelAPI, { ':id:': id }, null, null, null, true)
      .then(res => {
        setParcelData(res);
      })
      .catch((err) => {
        if (err === 'paymentTimeout') {
          setError( 'לקוח/ה יקר/ה, זמן הטיפול במשלוח הסתיים ולא ניתן לבצע תשלום. למידע נוסף אנא פנה/י אל אתר הרכישה.');
        } else {
          setError(`תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.`);
        }
      });
  }, [match]);

  function toggleDetails() {
    setShowForm(s => !s);
  }

  function areFieldsValid() {
    const errObj = {
      email: Validators.email(fields.email, emailErrorMessage),
      buyerID: Validators.idNumber(fields.buyerID, idNumberErrorMessage)
    };
    setErrors(errObj);

    if (Object.values(errObj).find(v => !!v)) {
      return false;
    }

    if (parcelData.buyer.isIDNeeded && !fields.buyerID) {
      return false;
    }

    return true;
  }

  function getUpdatedFields() {
    return Object.keys(fields).reduce((m, key) => {
      const value = fields[key];

      if (!value) {
        return m;
      }

      switch (key) {
        case 'email': {
          if (value !== parcelData.buyer.email) {
            m[key] = value;
          }
          break;
        }
        case 'buyerID': {
          m[key] = value;
          break;
        }
        default: {
          console.error(`Unexpected input field ${key}`);
        }
      }
      return m;
    }, {});
  }

  function updateParcel() {
    const updatedFields = getUpdatedFields();
    if (!Object.keys(updatedFields).length) {
      return Promise.resolve();
    }

    return apiService
      .callServer(
        updatePayDutiesParcelAPI,
        { ':id:': parcelId },
        null,
        JSON.stringify(updatedFields),
        null,
        true
      )
      .catch(() => {
        throw new UpdateError();
      });
  }

  function collectPayment() {
    const baseCreditGuardCallbackUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/parcels/creditguard/process_payment?page=payDuties&status=:status:`;
    const creditGuardCallbackUrls = {
      success_url: baseCreditGuardCallbackUrl.replace(':status:', 'success'),
      error_url: baseCreditGuardCallbackUrl.replace(':status:', 'error'),
      cancel_url: baseCreditGuardCallbackUrl.replace(':status:', 'cancelled')
    };

    const totalSum = parseFloat(parcelData.duties.localCurrency.payments.total);

    return apiService
      .callServer(
        collectPayDutiesParcelAPI,
        { ':id:': parcelId },
        null,
        JSON.stringify({ ...creditGuardCallbackUrls, total: totalSum, commLinkID, paidViaComm: linkBy  }),
        null,
        true
      )
      .then(res => {
        setCreditGuardUrl(res.url);
      })
      .catch(() => {
        throw new CollectPaymentError();
      });
  }

  const handleSubmit = () => {
    if (!areFieldsValid()) {
      return;
    }
    setSubmitted(true);

    updateParcel()
      .then(collectPayment)
      .catch((err) => {
        if (err instanceof ExelotError) {
          setError(err.message);
          return;
        }

        // Should not happen
        console.error(err);

        // Using a default error message
        setError(new UpdateError().message);
      });
  };

  function handleChangeInput(ev) {
    const { value, name } = ev.target;
    if (errors[name]) resetError(name);
    setValue && setValue(Parsers.sanitizeString(value) || '', name);
  }

  function renderError() {
    if (!errorMessage) {
      return null;
    }

    return <PayDutiesError text={errorMessage} />;
  }

  if (!parcelData) {
    return renderError();
  }

  if (creditGuardUrl) {
    return (
      <Iframe
        url={creditGuardUrl}
        className='cg-iframe'
      />
    );
  }

  return (
    <div className='payDuties'>
      {renderError()}
      <div>
        {showForm
          ? (
            <PayForm
              parcelData={parcelData}
              fields={fields}
              onChangeInput={handleChangeInput}
              errors={errors}
            />
          )
          : <PayDetails parcelData={parcelData} />}

        <div className='actions animated fadeInUp'>
          <div className='section explanation bold'>
            כדי למנוע עיכובים במכס וחיוב בדמי אחסנה<br />
            יש להסדיר תשלום בהקדם
          </div>
          <button className='btn orange' id='paymentDetailsBtn' onClick={toggleDetails}>
            <i className='fa fa-info' aria-hidden='true' />
            {showForm ? ' פרטי תשלום' :
              'פרטי משלוח'}
          </button>
          <button className='btn orange' onClick={handleSubmit} disabled={submitted || !fields.email || (parcelData.buyer.isIDNeeded && !fields.buyerID)}>
            <i className='fa fa-lock' aria-hidden='true' />
            לתשלום מאובטח
          </button>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  const {
    getPayDutiesParcel,
    updatePayDutiesParcel,
    collectPayDutiesParcel
  } = state.api;

  return {
    getPayDutiesParcelAPI: getPayDutiesParcel,
    updatePayDutiesParcelAPI: updatePayDutiesParcel,
    collectPayDutiesParcelAPI: collectPayDutiesParcel
  };
}

export default connect(mapStateToProps)(PayDuties);
